import React from 'react';
import { useTranslation } from 'react-i18next';
import { FarmOverviewRow } from '../../models/farms-overview';
import { useAnchorRect } from '../../hooks/useAnchorRect';
import { HoverCard } from '../../elements/ui/hover-card';
import { pigletRegistrationColors as c } from '../PigletRegistration/colors';

const num = (value: number) => value.toLocaleString();

export interface FarmAntibioticsBadgeProps {
  farm: FarmOverviewRow;
  /** Weeks the count covers, quoted in the hover text. */
  lookbackWeeks: number;
}

/**
 * Antibiotic treatments the farm registered over the lookback.
 *
 * Zero is the red one, and it is read as a registration problem rather than as good news: a pig farm that
 * gave no antibiotics at all for weeks is far less likely than one that gives them and does not record it.
 * Any count above zero is stated plainly, with no judgement - this is not a usage target.
 */
export default function FarmAntibioticsBadge({ farm, lookbackWeeks }: FarmAntibioticsBadgeProps) {
  const { t } = useTranslation();
  const wrapperRef = React.useRef<HTMLSpanElement>(null);
  const { anchor, openByPointer, openByFocus, clear } = useAnchorRect(wrapperRef);
  const count = farm.antibioticTreatmentCount;
  const none = count === 0;

  const tooltipKey = none ? 'f-farms-overview-antibiotics-none-title' : 'f-farms-overview-antibiotics-count-title';

  return (
    <span
      ref={wrapperRef}
      tabIndex={0}
      onMouseEnter={openByPointer}
      onMouseLeave={clear}
      onFocus={openByFocus}
      onBlur={clear}
      className={`inline-flex shrink-0 items-center gap-1.5 rounded-full border px-2.5 py-0.5 text-xs font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 ${
        none ? 'border-red-200 bg-red-50 text-red-700' : 'border-gray-200 bg-white text-gray-700'
      }`}
    >
      <span
        className="h-1.5 w-1.5 shrink-0 rounded-full"
        style={{ backgroundColor: none ? c.belowExpected : c.expected }}
      />
      {none
        ? t('f-farms-overview-antibiotics-none')
        : t('f-farms-overview-antibiotics-count', { count, value: num(count) })}

      {anchor && (
        <HoverCard anchor={anchor} className="max-w-72">
          <p className="text-gray-500">{t(tooltipKey, { weeks: lookbackWeeks })}</p>
        </HoverCard>
      )}
    </span>
  );
}
